import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebaseConfig';
import { auth } from './firebaseConfig';
import { PaymentMethod } from '../shared/model/schemas/checkout';
import { SHIPPING_FLAT } from '../shared/lib/price';
import { triggerBuyerOrderEmail, triggerSellerNewOrderEmail } from './emailService';
import { getFunctions } from 'firebase/functions';
import { sanitizeString, sanitizeError } from '../utils/sanitization';
import { validateString, validateNumber, validateArray, isValidDocumentId } from '../utils/validation';
import { verifyUserIdOwnership } from '../lib/security/idorProtection';
// AFFILIATE (disabled for now)
// import { getAttribution } from './affiliateTracking';

function sanitizeNumber(input: any, min: number = 0, max: number = 1000000): number | undefined {
  const num = Number(input);
  if (isNaN(num) || !isFinite(num)) return undefined;
  return Math.max(min, Math.min(max, num));
}

// Security: Enhanced rate limiting for order operations
class OrderRateLimiter {
  private attempts: Map<string, number[]> = new Map();
  private readonly maxAttempts = 5; // 5 operations per minute
  private readonly windowMs = 60000; // 1 minute
  private readonly maxAttemptsPerHour = 50; // 50 operations per hour
  private readonly hourlyWindowMs = 3600000; // 1 hour

  isAllowed(key: string): { allowed: boolean; message?: string } {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    
    // Remove old attempts outside the window
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    const hourlyAttempts = attempts.filter(time => now - time < this.hourlyWindowMs);
    
    // Check hourly limit first
    if (hourlyAttempts.length >= this.maxAttemptsPerHour) {
      return {
        allowed: false,
        message: 'Too many order operations in the last hour. Please wait before trying again.'
      };
    }
    
    if (recentAttempts.length >= this.maxAttempts) {
      return {
        allowed: false,
        message: 'Too many order operations. Please try again later.'
      };
    }
    
    recentAttempts.push(now);
    this.attempts.set(key, recentAttempts);
    return { allowed: true };
  }

  getRemainingAttempts(key: string): number {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    return Math.max(0, this.maxAttempts - recentAttempts.length);
  }
}

const orderRateLimiter = new OrderRateLimiter();

// Simple order creation - direct Firestore write like Flutter app
// REMOVED: createOrderDirect function - security risk
// All order creation now goes through secure Firebase Cloud Functions

// Security: Enhanced create order after payment verification with input validation and rate limiting
export async function createOrderAfterPayment(paymentData: {
  orderId?: string; // Optional - will be generated if not provided
  paymentMethod: PaymentMethod;
  transactionId: string;
  gatewayRef: string;
  checkoutData: any;
}): Promise<{ orderId: string; success: boolean; error?: string }> {
  try {
    // Security: Enhanced input validation
    const transactionIdValidation = validateString(paymentData.transactionId, 'Transaction ID', 1, 200);
    const gatewayRefValidation = validateString(paymentData.gatewayRef, 'Gateway Reference', 1, 200);
    
    if (!transactionIdValidation.valid) {
      throw new Error(transactionIdValidation.message || 'Invalid transaction ID provided');
    }
    
    if (!gatewayRefValidation.valid) {
      throw new Error(gatewayRefValidation.message || 'Invalid gateway reference provided');
    }

    const sanitizedTransactionId = sanitizeString(paymentData.transactionId, 200);
    const sanitizedGatewayRef = sanitizeString(paymentData.gatewayRef, 200);
    
    if (!sanitizedTransactionId || !sanitizedGatewayRef) {
      throw new Error('Invalid payment data provided');
    }
    
    // Generate orderId if not provided
    const sanitizedOrderId = paymentData.orderId 
      ? sanitizeString(paymentData.orderId, 100) 
      : `ORD${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    if (!sanitizedOrderId) {
      throw new Error('Failed to generate order ID');
    }
    
    // Wait for authentication state to be restored (important after payment redirects)
    let user = auth.currentUser;
    let attempts = 0;
    const maxAttempts = 10; // Wait up to 5 seconds
    
    while (!user && attempts < maxAttempts) {
      // SECURITY: Removed console.warn to prevent information leakage
      await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms
      user = auth.currentUser;
      attempts++;
    }
    
    if (!user) {
      // SECURITY: Removed console.warn to prevent information leakage
      throw new Error('User authentication not restored after payment. Please refresh the page and try again.');
    }
    
    // Security: Enhanced rate limiting
    const rateLimitKey = `create_order_${user.uid}`;
    const rateLimitResult = orderRateLimiter.isAllowed(rateLimitKey);
    if (!rateLimitResult.allowed) {
      throw new Error(rateLimitResult.message || 'Too many order creation attempts. Please try again later.');
    }

    // Note: Order ID will be generated by cloud function
    // Removed debug log to prevent sensitive data exposure (userId)
    

    // Security: Enhanced validate and sanitize checkout data
    const { checkoutData } = paymentData;
    
    if (!checkoutData || typeof checkoutData !== 'object') {
      throw new Error('Invalid checkout data provided');
    }
    
    // Security: Enhanced data size limits
    const MAX_ITEMS = 100;
    const MAX_ORDER_NOTES = 500;
    
    // Security: Enhanced sanitize checkout data with validation
    const itemsValidation = validateArray(checkoutData.items, 'Items', 1, MAX_ITEMS);
    if (!itemsValidation.valid) {
      throw new Error(itemsValidation.message || 'Invalid items data');
    }

    const sanitizedCheckoutData = {
      items: Array.isArray(checkoutData.items) ? checkoutData.items.slice(0, MAX_ITEMS) : [],
      totals: checkoutData.totals || {},
      paymentMethod: sanitizeString(checkoutData.paymentMethod, 50),
      shippingAddress: checkoutData.shippingAddress || {},
      billingAddress: checkoutData.billingAddress || {},
      billingAddressSameAsShipping: Boolean(checkoutData.billingAddressSameAsShipping),
      shippingMethodId: sanitizeString(checkoutData.shippingMethodId, 100),
      voucherCode: sanitizeString(checkoutData.voucherCode, 50),
      deliveryVoucherCode: sanitizeString(checkoutData.deliveryVoucherCode, 50),
      orderNotes: sanitizeString(checkoutData.orderNotes, MAX_ORDER_NOTES)
    };
    
    // Security: Enhanced validate totals with comprehensive validation
    const totals = sanitizedCheckoutData.totals;
    
    const totalAmountValidation = validateNumber(totals?.total, 'Total amount', 0, 1000000);
    const subtotalValidation = validateNumber(totals?.subtotal, 'Subtotal', 0, 1000000);
    const shippingFeeValidation = validateNumber(totals?.shippingFee, 'Shipping fee', 0, 10000);
    const discountValidation = validateNumber(totals?.discount, 'Discount', 0, 1000000);
    const coinsRedeemedValidation = validateNumber(totals?.coinsRedeemed, 'Coins redeemed', 0, 1000000);
    const finalAmountValidation = validateNumber(totals?.final, 'Final amount', 0, 1000000);
    
    if (!totalAmountValidation.valid) {
      throw new Error(totalAmountValidation.message || 'Invalid total amount');
    }
    
    if (!subtotalValidation.valid) {
      throw new Error(subtotalValidation.message || 'Invalid subtotal');
    }
    
    if (!shippingFeeValidation.valid) {
      throw new Error(shippingFeeValidation.message || 'Invalid shipping fee');
    }
    
    if (!discountValidation.valid) {
      throw new Error(discountValidation.message || 'Invalid discount');
    }
    
    if (!coinsRedeemedValidation.valid) {
      throw new Error(coinsRedeemedValidation.message || 'Invalid coins redeemed');
    }
    
    if (!finalAmountValidation.valid) {
      throw new Error(finalAmountValidation.message || 'Invalid final amount');
    }
    
    const totalAmount = sanitizeNumber(totals?.total, 0, 1000000) || 0;
    const subtotal = sanitizeNumber(totals?.subtotal, 0, 1000000) || 0;
    const shippingFee = sanitizeNumber(totals?.shippingFee, 0, 10000) || SHIPPING_FLAT;
    const discount = sanitizeNumber(totals?.discount, 0, 1000000) || 0;
    const coinsRedeemed = sanitizeNumber(totals?.coinsRedeemed, 0, 1000000) || 0;
    const finalAmount = sanitizeNumber(totals?.final, 0, 1000000) || 0;
    
    // Create order with verified payment status
    const orderDoc = {
      userId: user.uid,
      items: sanitizedCheckoutData.items,
      totalAmount,
      subtotal,
      shippingFee,
      discount,
      coinsRedeemed,
      finalAmount,
      paymentMethod: sanitizedCheckoutData.paymentMethod,
      payment_status: "completed",
      transactionId: sanitizedTransactionId,
      gatewayRef: sanitizedGatewayRef,
      shippingAddress: sanitizedCheckoutData.shippingAddress,
      billingAddress: sanitizedCheckoutData.billingAddress,
      billingAddressSameAsShipping: sanitizedCheckoutData.billingAddressSameAsShipping,
      shippingMethodId: sanitizedCheckoutData.shippingMethodId,
      voucherCode: sanitizedCheckoutData.voucherCode,
      orderNotes: sanitizedCheckoutData.orderNotes,
      status: "pending", // Order is pending but payment is completed
      returnStatus: "none",
      orderDate: serverTimestamp(),
      deliveryDate: null,
      shippingDate: null,
      paid_at: serverTimestamp(), // Mark as paid immediately
      statusHistory: {
        pending: serverTimestamp(),
        payment_completed: serverTimestamp()
      },
    };

    // SECURITY FIX: Use secure cloud function instead of direct Firestore write
    // This ensures proper validation, stock management, and security checks
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const { app } = await import('./firebaseConfig');
    const functions = getFunctions(app);
    const createOrderFn = httpsCallable(functions, 'createOrder');
    
    // Prepare order data in the format expected by the cloud function
    const orderPayload: any = {
      items: sanitizedCheckoutData.items.map((item: any) => ({
        productId: item.productId,
        price: item.price || 0,
        quantity: item.quantity || 1,
        selectedVariation: item.selectedVariation || {}
      })),
      shippingAddress: {
        fullName: sanitizedCheckoutData.shippingAddress?.fullName || sanitizedCheckoutData.shippingAddress?.Name || '',
        phone: sanitizedCheckoutData.shippingAddress?.phone || sanitizedCheckoutData.shippingAddress?.PhoneNumber || '',
        street: sanitizedCheckoutData.shippingAddress?.street || sanitizedCheckoutData.shippingAddress?.Street || '',
        city: sanitizedCheckoutData.shippingAddress?.city || sanitizedCheckoutData.shippingAddress?.City || '',
        district: sanitizedCheckoutData.shippingAddress?.district || sanitizedCheckoutData.shippingAddress?.District || '',
        province: sanitizedCheckoutData.shippingAddress?.province || sanitizedCheckoutData.shippingAddress?.State || '',
        country: sanitizedCheckoutData.shippingAddress?.country || sanitizedCheckoutData.shippingAddress?.Country || 'Nepal',
        postalCode: sanitizedCheckoutData.shippingAddress?.postalCode || ''
      },
      billingAddress: sanitizedCheckoutData.billingAddressSameAsShipping ? undefined : {
        fullName: sanitizedCheckoutData.billingAddress?.fullName || sanitizedCheckoutData.billingAddress?.Name || '',
        phone: sanitizedCheckoutData.billingAddress?.phone || sanitizedCheckoutData.billingAddress?.PhoneNumber || '',
        street: sanitizedCheckoutData.billingAddress?.street || sanitizedCheckoutData.billingAddress?.Street || '',
        city: sanitizedCheckoutData.billingAddress?.city || sanitizedCheckoutData.billingAddress?.City || '',
        district: sanitizedCheckoutData.billingAddress?.district || sanitizedCheckoutData.billingAddress?.District || '',
        province: sanitizedCheckoutData.billingAddress?.province || sanitizedCheckoutData.billingAddress?.State || '',
        country: sanitizedCheckoutData.billingAddress?.country || sanitizedCheckoutData.billingAddress?.Country || 'Nepal',
        postalCode: sanitizedCheckoutData.billingAddress?.postalCode || ''
      },
      billingAddressSameAsShipping: sanitizedCheckoutData.billingAddressSameAsShipping,
      shippingMethodId: sanitizedCheckoutData.shippingMethodId || 'standard',
      paymentMethod: sanitizedCheckoutData.paymentMethod || paymentData.paymentMethod,
      voucherCode: sanitizedCheckoutData.voucherCode || undefined,
      deliveryVoucherCode: sanitizedCheckoutData.deliveryVoucherCode || undefined,
      coinsToRedeem: coinsRedeemed || 0,
      idempotencyKey: checkoutData.idempotencyKey || `order-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };
    
    // Only include orderNotes if it's a non-empty string
    if (sanitizedCheckoutData.orderNotes && typeof sanitizedCheckoutData.orderNotes === 'string' && sanitizedCheckoutData.orderNotes.trim().length > 0) {
      orderPayload.orderNotes = sanitizedCheckoutData.orderNotes;
    }
    // If orderNotes is null/undefined/empty, omit it entirely (schema allows optional)

    // AFFILIATE (disabled for now): attribution (30-day window; from storage after payment redirect)
    // const attribution = getAttribution();
    // if (attribution?.affiliateId && isValidDocumentId(attribution.affiliateId)) {
    //   orderPayload.affiliateId = attribution.affiliateId;
    // }
    // if (attribution?.sessionId && attribution.sessionId.length > 0) {
    //   orderPayload.affiliateSessionId = attribution.sessionId;
    // }

    const result = await createOrderFn(orderPayload);
    const createdOrderId = (result.data as any)?.orderId;
    
    if (!createdOrderId) {
      throw new Error('Failed to create order through cloud function');
    }
    
    // Update order with payment details after creation
    const { updateDoc, doc: firestoreDoc } = await import('firebase/firestore');
    const { db } = await import('./firebaseConfig');
    await updateDoc(firestoreDoc(db, 'Orders', createdOrderId), {
      payment_status: 'paid',
      transactionId: sanitizedTransactionId,
      gatewayRef: sanitizedGatewayRef,
      paid_at: new Date(),
      status: 'processing'
    });
    
    // Use the order ID from cloud function
    const finalOrderId = createdOrderId;

    // Removed debug log to prevent sensitive data exposure (userId, orderId)

    // Clear user's cart after successful order
    try {
      const cartCollection = collection(db, 'Users', user.uid, 'Cart');
      const cartSnapshot = await getDocs(cartCollection);
      if (!cartSnapshot.empty) {
        const batch = writeBatch(db);
        cartSnapshot.docs.forEach(doc => batch.delete(doc.ref));
        await batch.commit();
        // Removed debug log to prevent sensitive data exposure (userId)
      }
    } catch (error) {
      // SECURITY: Removed console.warn to prevent information leakage
    }

    // Clear pending checkout data from session
    try {
      sessionStorage.removeItem('pendingCheckoutData');
    } catch (error) {
      // SECURITY: Removed console.warn to prevent information leakage
    }

    // Security: Enhanced send email notifications (non-blocking) with sanitized logging
    try {
      // Removed debug log to prevent sensitive data exposure (email addresses)
      
      // Send buyer order confirmation email
      if (user.email) {
        const emailValidation = validateString(user.email, 'User email', 1, 200);
        if (emailValidation.valid) {
          await triggerBuyerOrderEmail(user.email, finalOrderId);
          // Removed debug log to prevent sensitive data exposure (email addresses)
        }
      }

      // Send seller notifications for each unique seller
      const items = sanitizedCheckoutData.items || [];
      const sellerIds = new Set(items.map((item: any) => item.sellerId).filter(Boolean));
      
      // Removed debug log to prevent excessive console output
      
      for (const sellerId of sellerIds) {
        try {
          // Security: Enhanced validate seller ID
          if (!isValidDocumentId(sellerId as string)) {
            // SECURITY: Removed console.warn to prevent information leakage
            continue;
          }
          
          // Get seller email
          const sellerDoc = await getDoc(doc(db, 'Users', sellerId as string));
          const sellerData = sellerDoc.data();
          const sellerEmail = sellerData?.email;

          if (sellerEmail) {
            const sellerEmailValidation = validateString(sellerEmail, 'Seller email', 1, 200);
            if (sellerEmailValidation.valid) {
              // Get product name for the first item from this seller
              const sellerItem = items.find((item: any) => item.sellerId === sellerId);
              const productName = sanitizeString(sellerItem?.title, 100) || 'Product';
              
              await triggerSellerNewOrderEmail(sellerEmail, finalOrderId, productName);
              // Removed debug log to prevent sensitive data exposure (sellerId, email, orderId)
            }
          }
        } catch (sellerEmailError) {
          // SECURITY: Removed console.warn to prevent information leakage
        }
      }
    } catch (emailError) {
      // Don't fail the order if email fails
      // SECURITY: Removed console.warn to prevent information leakage
    }

    return { orderId: finalOrderId, success: true };
  } catch (error) {
    // Security: Enhanced error logging
    const errorMessage = sanitizeError(error);
    // SECURITY: Removed console.warn to prevent information leakage
    throw new Error(errorMessage);
  }
}

// Security: Enhanced get order by ID with input validation and authorization
export async function getOrderById(orderId: string): Promise<any> {
  try {
    // Security: Enhanced validate order ID
    const orderIdValidation = validateString(orderId, 'Order ID', 1, 100);
    if (!orderIdValidation.valid) {
      throw new Error(orderIdValidation.message || 'Invalid order ID provided');
    }

    if (!isValidDocumentId(orderId)) {
      throw new Error('Invalid order ID format');
    }
    
    // Removed verbose logging - order fetches happen frequently
    
    const orderDoc = await getDoc(doc(db, 'Orders', orderId));
    
    if (!orderDoc.exists()) {
      throw new Error('Order not found');
    }

    const orderData = orderDoc.data();
    
    // SECURITY: Enhanced check if user is authorized to view this order
    // Check both userId and buyerId fields (match backend behavior)
    const currentUser = auth.currentUser;
    if (currentUser) {
      const isBuyer = orderData.userId === currentUser.uid || orderData.buyerId === currentUser.uid;
      const isSeller = orderData.items?.some((item: any) => item.sellerId === currentUser.uid);
      
      // Check if user is admin
      let isAdmin = false;
      try {
        const userDoc = await getDoc(doc(db, 'Users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          isAdmin = userData?.role === 'admin';
        }
      } catch (error) {
        // If admin check fails, continue with ownership check only
        // SECURITY: Removed console.warn to prevent information leakage
      }
      
      if (!isBuyer && !isSeller && !isAdmin) {
        // SECURITY: Log unauthorized access attempt
        // SECURITY: Removed console.warn to prevent information leakage
        throw new Error('Access denied - cannot view another user\'s order');
      }
    }
    
    // Removed verbose logging - order fetches happen frequently
    
    return orderData;
  } catch (error) {
    // Security: Enhanced error logging
    const errorMessage = sanitizeError(error);
    // SECURITY: Removed console.warn to prevent information leakage
    throw new Error(errorMessage);
  }
}

// Security: Get user orders with input validation and authorization
// SECURITY: IDOR Protection - Only returns orders for authenticated user
export async function getUserOrders(userId: string): Promise<any[]> {
  try {
    // Security: Validate user ID
    if (!isValidDocumentId(userId)) {
      throw new Error('Invalid user ID provided');
    }
    
    // SECURITY: IDOR Protection - Verify userId matches authenticated user
    // Special case: Admins can view any user's orders
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to view orders');
    }
    
    // Check if user is admin (admins can bypass IDOR check for orders)
    let isAdmin = false;
    try {
      const userDoc = await getDoc(doc(db, 'Users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        isAdmin = userData?.role === 'admin';
      }
    } catch (error) {
      // If admin check fails, continue with ownership check only
    }
    
    // Only enforce IDOR if user is not admin
    if (!isAdmin) {
      try {
        verifyUserIdOwnership(userId, {
          action: 'view orders',
          resourceType: 'orders'
        });
      } catch (error: any) {
        throw new Error(error.message || 'Access denied - cannot view another user\'s orders');
      }
    }
    
    // SECURITY: Query both userId and buyerId fields to get all user orders
    // Note: Firestore doesn't support OR queries directly, so we query userId
    // Orders with buyerId should also have userId set, but we check both in getOrderById
    const ordersQuery = query(
      collection(db, 'Orders'),
      where('userId', '==', userId),
      orderBy('orderDate', 'desc')
    );
    
    const ordersSnapshot = await getDocs(ordersQuery);
    const orders = ordersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return orders;
  } catch (error) {
    // SECURITY: Removed console.warn to prevent information leakage
    throw new Error(sanitizeError(error));
  }
}

// REMOVED: Legacy functions - getOrderByIdLegacy and getUserOrdersLegacy
// Use the main functions getOrderById and getUserOrders directly

// Security: Initiate return for an order with input validation and rate limiting
export async function initiateReturn(orderId: string, returnReason: string, returnItems: string[]): Promise<{ success: boolean; message: string }> {
  try {
    // Security: Validate input parameters
    if (!isValidDocumentId(orderId)) {
      throw new Error('Invalid order ID provided');
    }
    
    const sanitizedReturnReason = sanitizeString(returnReason, 500);
    if (!sanitizedReturnReason) {
      throw new Error('Invalid return reason provided');
    }
    
    if (!Array.isArray(returnItems) || returnItems.length === 0) {
      throw new Error('Invalid return items provided');
    }
    
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    // Security: Rate limiting
    const rateLimitKey = `initiate_return_${user.uid}`;
    if (!orderRateLimiter.isAllowed(rateLimitKey)) {
      throw new Error('Too many return requests. Please try again later.');
    }

    // SECURITY: Use backend callable function instead of direct Firestore write
    // This ensures proper validation, security checks, and business logic
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const functions = getFunctions();
    const initiateReturnFunction = httpsCallable(functions, 'initiateReturn');
    
    const result = await initiateReturnFunction({
      orderId: orderId,
      returnReason: sanitizedReturnReason,
      returnItems: returnItems
      // Note: userId is NOT sent - backend extracts from auth token
    });

    const data = result.data as any;

    return {
      success: data.success || true,
      message: data.message || 'Return request submitted successfully. Our team will review and contact you within 24 hours.'
    };

  } catch (error) {
    // SECURITY: Removed console.warn to prevent information leakage
    throw new Error(sanitizeError(error));
  }
}

/**
 * Cancel an order
 * SECURITY: Uses Firebase callable function for proper authentication
 */
export async function cancelOrder(orderId: string, reason?: string): Promise<{ success: boolean; message: string }> {
  try {
    // Validate inputs
    if (!orderId || typeof orderId !== 'string') {
      throw new Error('Valid order ID is required');
    }

    if (!isValidDocumentId(orderId)) {
      throw new Error('Invalid order ID format');
    }

    const sanitizedReason = reason ? sanitizeString(reason, 500) : 'No reason provided';

    // Get current user
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to cancel orders');
    }

    // SECURITY: Use Firebase callable function instead of direct HTTP
    // This ensures proper authentication via Firebase Auth token
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const functions = getFunctions();
    const cancelOrderFunction = httpsCallable(functions, 'cancelOrder');
    
    const result = await cancelOrderFunction({
      orderId: orderId,
      reason: sanitizedReason
      // Note: uid is NOT sent - backend extracts from auth token
    });

    const data = result.data as any;

    return {
      success: data.success || true,
      message: data.message || 'Order cancelled successfully'
    };

  } catch (error) {
    // SECURITY: Removed console.error to prevent information leakage
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to cancel order'
    };
  }
}
